import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CsvBoxImportButton,
  type CsvBoxImportMode,
} from '@/components/csv-box-import-button';
import { Badge } from '@/components/ui/badge';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { type ImportSource } from '@/lib/analytics-events';
import { isDevelopment } from '@/lib/config';
import { GenerateSaleExportContext } from '@/lib/contexts/generate-sale-export.context';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { ArrowLeft, BookPlus, Package, Search } from 'lucide-react';
import { Upload } from 'lucide-react';

import { Shopify } from '../icons/shopify';
import { BaseImportViewProps } from './import-view-props';

interface ViewProductsMenuProps extends BaseImportViewProps {
  isInternalUser: boolean;
  isDeveloper: boolean;
  products?: DealItemWithVersions[];
  dealId?: string;
  onRefetch?: () => Promise<void>;
  onCloseDialog?: () => void;
  hasShopifyPlugin: boolean;
  canHaveShopifyPlugin: boolean;
}

export const ViewProductsMenu: React.FC<ViewProductsMenuProps> = ({
  setImportView,
  isInternalUser,
  isDeveloper,
  products = [],
  dealId,
  onRefetch,
  onCloseDialog,
  hasShopifyPlugin,
  canHaveShopifyPlugin,
  onPricingModeRequested,
}) => {
  const { t } = useTranslation();
  const generateSaleExport = useContext(GenerateSaleExportContext);
  const hasProducts = products.length > 0;
  // Mode de l'import CSVBox ouvert directement depuis le menu, null = fermé.
  const [csvBoxMode, setCsvBoxMode] = useState<CsvBoxImportMode | null>(null);
  const shouldShowWebsiteScrapingSource =
    generateSaleExport ||
    isDeveloper ||
    (isInternalUser && (isDevelopment || !hasShopifyPlugin));

  const trackSourceSelected = (source: ImportSource) => {
    trackEvent('IMPORT_SOURCE_SELECTED', { dealId, source });
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          {!generateSaleExport && (
            <button
              className="rounded-md p-1 hover:bg-muted"
              onClick={() => setImportView('chooser')}
              aria-label="Revenir au choix"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}

          <DialogTitle>
            {t(
              generateSaleExport
                ? 'components.import_flow.products_menu.generation_title'
                : 'components.import_flow.products_menu.title'
            )}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t(
            generateSaleExport
              ? 'components.import_flow.products_menu.generation_description'
              : 'components.import_flow.products_menu.description'
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-6 space-y-3">
        {(hasShopifyPlugin || canHaveShopifyPlugin) && (
          <button
            className="flex w-full items-center gap-4 rounded-lg border p-4 hover:bg-muted"
            onClick={() => {
              trackSourceSelected('shopify');
              if (canHaveShopifyPlugin) {
                setImportView('installShopifyPlugin');
              } else {
                setImportView('productsShopifyOptionSelection');
              }
            }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted">
              <Shopify fill="black" />
            </div>
            <div className="flex flex-col text-left">
              <div className="text-base font-medium">
                {t(
                  canHaveShopifyPlugin
                    ? 'components.import_flow.products_menu.shopify_install'
                    : 'components.import_flow.products_menu.shopify_import'
                )}
              </div>
              {canHaveShopifyPlugin ? (
                <div className="text-sm text-gray-500">
                  {t(
                    'components.import_flow.products_menu.shopify_import_subtitle'
                  )}
                </div>
              ) : null}
            </div>
            <Badge variant="default" className="ml-auto">
              {t('general.recommended')}
            </Badge>
          </button>
        )}
        <button
          className="flex w-full items-center gap-4 rounded-lg border p-4 hover:bg-muted"
          onClick={() => {
            trackSourceSelected('previousCampaign');
            if (hasProducts) {
              setImportView('productsCopyPreviousSalesOptionSelection');
            } else {
              setImportView('productsCopyPreviousSales');
            }
          }}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted">
            <Package className="h-6 w-6" />
          </div>
          <div className="flex flex-col text-left">
            <div className="text-base font-medium">
              {t('components.import_flow.copy_previous_sales.title')}
            </div>
          </div>
        </button>
        {shouldShowWebsiteScrapingSource && (
          <button
            className="flex w-full items-center gap-4 rounded-lg border p-4 hover:bg-muted"
            onClick={() => {
              trackSourceSelected('website');
              if (hasProducts) {
                setImportView('productsScrapingOptionSelection');
              } else {
                setImportView('productsScraping');
              }
            }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted">
              <Search className="h-6 w-6" />
            </div>
            <div className="flex flex-col text-left">
              <div className="text-base font-medium">
                {t('components.import_flow.products_menu.scrape_website')}
              </div>
            </div>
          </button>
        )}

        {!generateSaleExport && (
          <button
            className="flex w-full items-center gap-4 rounded-lg border p-4 hover:bg-muted"
            onClick={(event) => {
              // Internes Choose : le clic simple ouvre la synchronisation par ID
              // deal item ; Alt/Option + clic garde le flux legacy « Ajouter /
              // Mettre à jour ». Les marques restent sur le flux legacy.
              if (isInternalUser && !event.altKey) {
                trackSourceSelected('csvSync');
                setCsvBoxMode({ kind: 'sync' });
                return;
              }
              trackSourceSelected('csv');
              if (hasProducts) {
                setImportView('productsImportCSVOptionSelection');
              } else {
                setCsvBoxMode({
                  kind: 'legacy',
                  mode: 'add',
                  anchorKey: 'auto',
                });
              }
            }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted">
              <Upload className="h-6 w-6" />
            </div>
            <div className="flex flex-col text-left">
              <div className="text-base font-medium">
                {t('general.import_csv_file')}
              </div>
            </div>
          </button>
        )}

        {!generateSaleExport && (
          <button
            className="flex w-full items-center gap-4 rounded-lg border p-4 hover:bg-muted"
            onClick={() => setImportView('productsManual')}
            data-testid="manuel-import-button"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted">
              <BookPlus className="h-6 w-6" />
            </div>
            <div className="flex flex-col text-left">
              <div className="text-base font-medium">
                {t(
                  isInternalUser
                    ? 'components.import_flow.products_menu.add_manually_or_url'
                    : 'components.import_flow.products_menu.add_manually'
                )}
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Ouvre CsvBox immédiatement et sans UI : ajout sur une offre vide, ou
          synchronisation par ID deal item (Option + clic). */}
      {dealId && csvBoxMode && (
        <CsvBoxImportButton
          dealId={dealId}
          importMode={csvBoxMode}
          autoOpen
          hidden
          onUploadSuccess={async () => {
            setCsvBoxMode(null);
            await onRefetch?.();
          }}
          onCloseModal={() => {
            setCsvBoxMode(null);
            onCloseDialog?.();
          }}
          onPricingModeRequested={
            generateSaleExport ? undefined : onPricingModeRequested
          }
        />
      )}
    </>
  );
};
